import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SpaIcon from '@mui/icons-material/Spa';
import HotelIcon from '@mui/icons-material/Hotel';
import ApartmentIcon from '@mui/icons-material/Apartment';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import StorefrontIcon from '@mui/icons-material/Storefront';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import { ROUTES } from '../../app/routes';
import { breadcrumbJsonLd, absoluteUrl, usePageMeta } from '../../core/usePageMeta';
import { PublicLayout, usePublicLanguage } from './components';
import { NotFoundPage } from './NotFoundPage';
import { PRODUCTS, productBrowsePath, productPath, productSite, type CatalogProduct } from './products';

const ICONS: Record<string, typeof StorefrontIcon> = {
  Restaurant: RestaurantIcon, Spa: SpaIcon, Hotel: HotelIcon, Apartment: ApartmentIcon, Work: WorkOutlineIcon,
};

function ProductIcon({ product, size = 26 }: { product: CatalogProduct; size?: number }) {
  const Icon = ICONS[product.icon] ?? StorefrontIcon;
  return <Icon sx={{ fontSize: size }} aria-hidden />;
}

function useList(key: string): string[] {
  const { t } = useTranslation();
  const value = t(key, { returnObjects: true, defaultValue: [] }) as unknown;
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function useFaq(key: string): { q: string; a: string }[] {
  const { t } = useTranslation();
  const value = t(key, { returnObjects: true, defaultValue: [] }) as unknown;
  return Array.isArray(value) ? value.filter((item) => item && typeof item.q === 'string' && typeof item.a === 'string') : [];
}

function StatusBadge({ product }: { product: CatalogProduct }) {
  const { t } = useTranslation();
  return <span className="hub-product-status" data-status={product.status}>
    {product.status === 'live' ? t('hub.products.live', 'Available') : t('hub.products.soon', 'Coming soon')}
  </span>;
}

/** `/products` — every Biznesi product, live and upcoming. */
export function ProductsPage() {
  const { t } = useTranslation();
  const lang = usePublicLanguage();
  const home = t('hub.seo.breadcrumbHome', 'Home');
  usePageMeta(t('hub.products.seoTitle', 'Products — software for restaurants, salons, hotels and hiring'), {
    description: t('hub.products.seoDescription'),
    path: ROUTES.products,
    lang,
    jsonLd: [
      breadcrumbJsonLd([{ name: home, path: '/' }, { name: t('hub.nav.products', 'Products'), path: ROUTES.products }]),
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: t('hub.products.title', 'Biznesi products'),
        itemListElement: PRODUCTS.map((product, index) => ({
          '@type': 'ListItem', position: index + 1, url: absoluteUrl(productPath(product.slug)),
          name: t(`hub.products.items.${product.key}.name`, product.key),
        })),
      },
    ],
  });

  return <PublicLayout>
    <section className="hub-hero"><div className="hub-wrap">
      <h1>{t('hub.products.title', 'Biznesi products')}</h1>
      <p>{t('hub.products.subtitle')}</p>
    </div></section>
    <div className="hub-wrap hub-section">
      <div className="hub-grid">
        {PRODUCTS.map((product) => <article key={product.slug} className="hub-card hub-product-card">
          <div className="pad">
            <div className="row">
              <span className="hub-product-icon"><ProductIcon product={product} /></span>
              <StatusBadge product={product} />
            </div>
            <h3><Link to={productPath(product.slug)}>{t(`hub.products.items.${product.key}.name`, product.key)}</Link></h3>
            <span className="hub-card-line">{t(`hub.products.items.${product.key}.tagline`)}</span>
          </div>
        </article>)}
      </div>
      <div className="hub-banner">
        <div>
          <h2>{t('hub.business.title', 'Run your business on Biznesi')}</h2>
          <p>{t('hub.business.subtitle')}</p>
        </div>
        <Link className="hub-btn" to={ROUTES.forBusiness}>{t('hub.nav.forBusiness', 'For business')}</Link>
      </div>
    </div>
  </PublicLayout>;
}

/** `/products/:product` — one product: what customers get, what the business gets, FAQ. */
export function ProductPage() {
  const { product: slug } = useParams();
  const product = PRODUCTS.find((item) => item.slug === slug);
  return product ? <ProductDetail product={product} /> : <NotFoundPage />;
}

function ProductDetail({ product }: { product: CatalogProduct }) {
  const { t } = useTranslation();
  const lang = usePublicLanguage();
  const base = `hub.products.items.${product.key}`;
  const name = t(`${base}.name`, product.key);
  const customers = useList(`${base}.customers`);
  const business = useList(`${base}.business`);
  const faq = useFaq(`${base}.faq`);
  const site = productSite(product);
  const browse = productBrowsePath(product);
  const path = productPath(product.slug);
  const description = t(`${base}.seoDescription`);

  usePageMeta(t(`${base}.seoTitle`, name), {
    description,
    path,
    lang,
    jsonLd: [
      breadcrumbJsonLd([
        { name: t('hub.seo.breadcrumbHome', 'Home'), path: '/' },
        { name: t('hub.nav.products', 'Products'), path: ROUTES.products },
        { name, path },
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: `Biznesi ${name}`,
        description,
        url: absoluteUrl(path),
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        publisher: { '@type': 'Organization', name: 'Biznesi', url: absoluteUrl('/') },
      },
      ...(faq.length ? [{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
      }] : []),
    ],
  });

  return <PublicLayout>
    <section className="hub-hero"><div className="hub-wrap">
      <nav className="hub-breadcrumbs" aria-label={t('hub.seo.breadcrumbs', 'Breadcrumbs')}>
        <Link to={ROUTES.landing}>{t('hub.seo.breadcrumbHome', 'Home')}</Link>
        <span aria-hidden>›</span>
        <Link to={ROUTES.products}>{t('hub.nav.products', 'Products')}</Link>
      </nav>
      <span className="hub-product-icon lg"><ProductIcon product={product} size={34} /></span>
      <h1>{t(`${base}.title`, name)}</h1>
      <p>{t(`${base}.description`)}</p>
      <div className="hub-hero-meta">
        {browse && product.status === 'live' && <Link className="hub-btn" to={browse}>{t(`${base}.browse`, name)}</Link>}
        {site && product.status === 'live' && <a className="hub-btn ghost" href={site} rel="noopener">{t('hub.products.open', 'Open {{name}}', { name })}<NorthEastIcon sx={{ fontSize: 16 }} aria-hidden /></a>}
        {product.status === 'soon' && <Link className="hub-btn" to={ROUTES.register}>{t('hub.products.notify', 'Create an account to be notified')}</Link>}
        <StatusBadge product={product} />
      </div>
      {product.status === 'soon' && <p className="hub-hero-note">{t('hub.products.comingText')}</p>}
    </div></section>

    <div className="hub-wrap hub-section">
      <div className="hub-product-columns">
        <section className="hub-card"><div className="pad">
          <h2>{t('hub.products.forCustomers', 'For customers')}</h2>
          <ul className="hub-product-list">{customers.map((item) => <li key={item}>{item}</li>)}</ul>
        </div></section>
        <section className="hub-card"><div className="pad">
          <h2>{t('hub.products.forBusiness', 'For the business')}</h2>
          <ul className="hub-product-list">{business.map((item) => <li key={item}>{item}</li>)}</ul>
        </div></section>
      </div>
    </div>

    {faq.length > 0 && <div className="hub-wrap hub-section">
      <h2 className="hub-page-title">{t('hub.products.faq', 'Frequently asked questions')}</h2>
      <div className="hub-product-faq">
        {faq.map((item) => <details key={item.q}><summary>{item.q}</summary><p>{item.a}</p></details>)}
      </div>
    </div>}

    <div className="hub-wrap hub-section">
      <h2 className="hub-page-title">{t('hub.products.other', 'Other products')}</h2>
      <nav className="hub-verticals" aria-label={t('hub.products.other', 'Other products')}>
        {PRODUCTS.filter((item) => item.slug !== product.slug).map((item) => <Link key={item.slug} className="hub-chip" to={productPath(item.slug)}>
          {t(`hub.products.items.${item.key}.name`, item.key)}
        </Link>)}
      </nav>
      <div className="hub-banner">
        <div>
          <h2>{t('hub.business.title', 'Run your business on Biznesi')}</h2>
          <p>{t('hub.business.subtitle')}</p>
        </div>
        <Link className="hub-btn" to={ROUTES.register}>{t('hub.business.cta', 'Create an account')}</Link>
      </div>
    </div>
  </PublicLayout>;
}
